'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/neural-nets'

const NeuronExplorer = dynamic(() => import('../_components/widgets/NNWidgets').then(m => m.NeuronExplorer), { ssr: false })

export default function TheNeuron() {
  return (
    <Lesson>
      <Step>
        <H>Loosely inspired by the brain</H>
        <P>
          A brain cell (a neuron) receives signals from other cells, and if the total stimulation is strong enough, it fires its
          own signal onward. In 1943 two scientists sketched a mathematical caricature of this, and almost every AI system today
          is built from millions of copies of it.
        </P>
        <P>
          The artificial neuron is something you already know. It takes some <Hl c="blue">inputs</Hl>, multiplies each by a{' '}
          <Hl c="green">weight</Hl>, adds them up with a <Hl c="green">bias</Hl>, and passes the total through an{' '}
          <strong>activation function</strong>:
        </P>
        <MB>{'\\amber{a} = f\\big(\\green{w_1}\\blue{x_1} + \\green{w_2}\\blue{x_2} + \\dots + \\green{b}\\big) = f(\\green{\\mathbf{w}}\\cdot\\blue{\\mathbf{x}} + \\green{b})'}</MB>
        <P>A dot product, plus a bias, through a squish. That’s the whole thing.</P>
      </Step>

      <Step gate="q-neuron">
        <H>Take one apart</H>
        <Figure n="7.1" wide caption="A neuron with two inputs. Change the inputs, weights, bias and activation. The heat map at the bottom shows its output for every possible pair of inputs — a neuron draws a soft line across the plane.">
          <NeuronExplorer />
        </Figure>
        <Quiz
          id="q-neuron"
          question="With a sigmoid activation, what does a single neuron do to the input plane?"
          options={[
            { label: 'Splits it with a soft straight line: high on one side, low on the other.', correct: true },
            { label: 'Draws a circle around the important region.', why: 'A single neuron only sees w·x + b — a straight line. Curved shapes need several neurons.' },
            { label: 'Outputs the same value everywhere.', why: 'Only if all weights are zero.' },
          ]}
          explain="One sigmoid neuron is exactly logistic regression from Module 6. Networks get their power from combining many of these lines."
        />
      </Step>

      <Step gate="q-act">
        <H>Activation functions</H>
        <P>The activation decides how the neuron responds to its total input <M>z</M>. The common ones:</P>
        <ul className="gd-list">
          <li>
            <strong>Step</strong> — fire (1) or don’t (0). The original idea, but its slope is zero almost everywhere, so
            gradient descent can’t learn through it.
          </li>
          <li>
            <strong>Sigmoid</strong> <M>{'\\sigma(z)'}</M> — a smooth step from 0 to 1.
          </li>
          <li>
            <strong>tanh</strong> — like sigmoid but from −1 to 1, centred on zero.
          </li>
          <li>
            <strong>ReLU</strong> <M>{'\\max(0, z)'}</M> — zero for negatives, the input itself for positives. Cheap, simple,
            and the workhorse of modern networks.
          </li>
        </ul>
        <Quiz
          id="q-act"
          question="Why can’t a network learn with the step activation and gradient descent?"
          options={[
            { label: 'Its slope is zero everywhere (except one jump), so the gradient gives no direction to move in.', correct: true },
            { label: 'It’s too slow to compute.', why: 'It’s the cheapest function there is. The problem is the slope.' },
            { label: 'It only outputs 0 or 1.', why: 'Outputting 0/1 is fine for a classifier — the flat slope is what kills learning.' },
          ]}
          explain="Gradient descent needs a slope to follow. Smooth activations like sigmoid, tanh and ReLU provide one."
        />
      </Step>

      <Step gate="neuron">
        <H>Build a neuron</H>
        <Exercise
          id="neuron"
          title="relu, sigmoid and neuron"
          {...EX.neuron}
          hints={[
            <>
              <C>Math.max(0, z)</C> and <C>1 / (1 + Math.exp(-z))</C>.
            </>,
            <>
              Start <C>z</C> at <C>b</C>, add <C>w[i] * x[i]</C> for each input, then return <C>activation(z)</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          Why is the activation so important? Without it, stacking neurons is pointless: a weighted sum of weighted sums is
          still just one weighted sum — a straight line. The nonlinearity is what lets layers build curves. Next lesson.
        </Callout>
      </Step>
    </Lesson>
  )
}
